import { UsageService } from '../../../../core/usage/usage.service';
import { FormValidationService } from './../../../../core/services/form-validation.service';
import { ADMISSION_SHEET_DIALOG_CONFIG } from '../../../../shared/config/floating-ai-dialog.config';
import { ageFromDob, sexFromPatient, toOnsetDate } from 'src/app/shared/util/icd-coding-context.util';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { EMPTY, Observable, Subscription, catchError, combineLatest, firstValueFrom, debounceTime, delay, distinctUntilChanged, map, of, startWith, switchMap, takeUntil, tap, throwError } from 'rxjs';
import { COUNTRIES, TITLES } from 'src/app/core/data/app.data';
import { GroupService } from 'src/app/core/services/group.service';
import { HospitalService } from 'src/app/core/services/hospital.service';
import { ScrollPositionService } from 'src/app/core/services/scroll-position.service';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FileObject } from './../../../../core/interfaces/file-object';
import { AIService } from './../../../../core/services/ai.service';
import { AnalyticsService } from './../../../../core/services/analytics.service';
import { IcdCode } from './../../../../core/services/app-data.service';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientBillingService } from './../../../../core/services/patient-billing.service';
import { PatientService } from './../../../../core/services/patient.service';
import { TokenUsageService } from './../../../../core/services/token-usage.service';
import { UserService } from './../../../../core/services/user.service';
import { SaIdValidator } from './../../../../shared/validators/sa-id.validator';
import { SpeechRecognitionService } from 'src/app/core/services/speech-recognition.service';
import { checkEmailTypo } from 'src/app/core/utils/email-typo.util';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormUtils } from 'src/app/core/utils/form.utils';
import { PatientNoteService } from 'src/app/core/services/patient-note.service';
import { phoneNumberValidator } from 'src/app/shared/components/intl-phone-input/intl-phone-input.validator';
import { environment } from 'src/environments/environment';
import { SchemeReferenceService } from 'src/app/core/scheme-reference/scheme-reference.service';
import { MedicalAidPick, medicalAidFields } from 'src/app/core/scheme-reference/scheme-pick';
import { schemeCodeOf } from 'src/app/core/utils/scheme-code';
import { patientGroupId } from 'src/app/core/utils/patient-group';
import { resolveScannedMedicalAid } from 'src/app/core/scheme-reference/scheme-match';
import { MedicalAidPickerInitial } from 'src/app/shared/components/medical-aid-picker/medical-aid-picker.component';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

export interface Scheme {
  name: string;
  code: string;
  schemeAdministratorCode: string;
  managedCareCompanyCode: string;
  captitionScheme: string;
  discontinuedDate: string;
  cmsReferenceNumber: string;
  category: string;
  schemeTelephoneNumber: string;
  schemeEmail: string;
  schemeUrl: string;
}

export interface Plan {
  code: string;
  schemeCode: string;
  option: string;
  remarks: string;
  discontinuedDate: string;
  startDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlanOption {
  code: string;
  planCode: string;
  option: string;
  remarks: string;
  balanceBilling: boolean;
  discontinuedDate: string;
  outHospitalReimbursementRate: string;
  pmbReimbursementRate: string;
  inHospitalReimbursementRate: string;
  insuranceCode: string;
  capitationOption: string;
}

@Component({
  selector: 'app-patient-detail',
  templateUrl: './patient-detail.component.html',
  styleUrls: ['./patient-detail.component.scss'],
})
export class PatientDetailComponent extends BaseComponent implements OnInit, OnDestroy {
  /** Marks where this work starts or finishes, for REMOAT's usage report. */
  private readonly usage = inject(UsageService);

  // ── ICD-10 coding-rule context (shared/util/icd-coding-context.util.ts) ──────────
  //
  // Feeds the code selector's rules. Each returns null when the value is unknown, and a rule
  // with no context stays SILENT rather than guessing — a false alarm reads as the system not
  // knowing the patient, which costs trust in every other finding.
  get icdCodingSex(): string | null { return sexFromPatient(this.patient); }
  get icdCodingAge(): number | null { return ageFromDob(this.patient?.dob); }

  /** Dates a newly added code from the admission this record was captured against — never from today. */
  get icdDefaultOnsetDate(): string | null { return toOnsetDate(this.patient?.admissionDate); }

  private subscriptions: Subscription[] = [];
  todayDate: moment.Moment = moment();
  form!: FormGroup;
  titles = TITLES;
  countries = COUNTRIES;
  patient: any;
  groupId!: string;
  hospitals: any[] = [];
  wards: any[] = [];
  selectedIcdCodes: IcdCode[] = [];
  selectedAllergies: any[] = [];
  selectedMedicalHistory: any[] = [];
  selectedMedicationHistory: any[] = [];
  selectedMedicalTeam: any[] = [];
  patientAuthNumbers: any[] = [];
  redirectTo?: string;
  redirectDate?: Date;
  redirectTimeSlot?: string;
  /** The doctor of the calendar column a new patient was booked from, passed on to the booking form. */
  redirectPhysicianId?: string;
  patientProcedureCodes: any[] | null = [];
  visibleDiagnosisFields: string[] = [];
  tokenBalance?: number;
  textMessageBalance?: number;
  isPremium: boolean = false;
  lockProcessing: boolean = false;
  groupUsers: any[] = [];
  emailSuggestion: string | null = null;
  user: any;
  hasQuickAddParam: boolean = false;
  /**
   * §30. The attachment picker's selection for a NEW INVITE.
   *
   * Always an array, because the panel that sets it always renders the picker for an invite — so
   * an empty one genuinely means "the user unticked everything", which is a different instruction
   * to the server than sending no field at all (§14.3: configuration pre-ticks, it never forces).
   */
  selectedDocumentIds: string[] = [];

  isQuickAdd: boolean = false;
  noContactDetails: boolean = false;
  isLoading: boolean = false;
  private hasClearedZ00Codes: boolean = false;

  // Section collapse states
  sectionExpandedStates = {
    clinical: true,
    personal: true,
    contact: true,
    medicalInsurance: true,
    personResponsible: true,
    authorizations: false, // Collapsed by default
    nextOfKin: false, // Collapsed by default
    medicalInfo: false, // Collapsed by default
    medicalTeam: false, // Collapsed by default
  };

  @ViewChild('scanPatientDetailsDialog') scanPatientDetailsDialog?: TemplateRef<any>;
  @ViewChild('medicalAidInformation') medicalAidInformation?: TemplateRef<any>;

  // Scheme and plan selection properties
  schemes: Scheme[] = [];
  selectedScheme?: Scheme;
  filteredSchemes!: Observable<Scheme[]>;

  oldMedicalSchemeRecord: any;

  plans: Plan[] = [];
  selectedPlan?: Plan;
  filteredPlans!: Observable<Plan[]>;

  plansOption: PlanOption[] = [];
  selectedPlanOption?: PlanOption;
  filteredPlanOptions!: Observable<PlanOption[]>;

  readonly schemeReference = inject(SchemeReferenceService);
  /** The patient as the medical aid picker starts on; replaced for each patient shown. */
  medicalAidInitial: MedicalAidPickerInitial | null = null;
  /** What the picker chose, saved over the form's medical aid; null until something is picked. */
  private medicalAidChanges: ReturnType<typeof medicalAidFields> | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private groupService: GroupService,
    private hospitalService: HospitalService,
    private patientService: PatientService,
    private patientBillingService: PatientBillingService,
    private scrollPositionService: ScrollPositionService,
    private aiService: AIService,
    private usageService: TokenUsageService,
    private analyticsService: AnalyticsService,
    private speechRecognitionService: SpeechRecognitionService,
    private validationService: FormValidationService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private patientNoteService: PatientNoteService,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);

    this.hospitalService.hospitals$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(hospitals => {
        this.hospitals = hospitals;
      });

    this.patientBillingService.patientBilling$
      .pipe(takeUntil(this.destroy$))
      .subscribe((billing: any[] | null) => {
        this.patientProcedureCodes = billing;
      });

    this.usageService.tokenBalance$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.tokenBalance = result;
      });

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: any) => {
        this.user = user;
      })

    this.usageService.textMessageCreditBalance$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.textMessageBalance = result;
      });

    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        this.groupUsers = users?.filter(u => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50');
      });

    /* const patientId = this.route.snapshot.paramMap.get('id');
    if (patientId) {
      this.patientService.getPatientById(patientId)
        .pipe(
          takeUntil(this.destroy$)
        ).subscribe(); // Trigger loading the patient
    } */
  }

  ngOnInit() {
    this.initForm();
    this.setupStatusChangeListener();

    // Simulate asynchronous form initialization
    /*  this.fetchInitialData().subscribe(() => {
       this.setupValueChangesListeners(); // Set up valueChanges listeners after initialization
       //this.form.get('medicalSchemeCode')?.setValue('', { emitEvent: true }); // Set initial value
     }); */



    this.groupService.selectedGroup$
      .pipe(
        switchMap(selectedGroup => {
          if (selectedGroup) {
            this.groupId = selectedGroup.id;
            this.isPremium = selectedGroup.isPremium === true;
            this.form.patchValue({ groupId: patientGroupId(this.patient, this.groupId) });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();


    this.patientService.currentPatient$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(patient => {
        this.patient = patient;
        // Reset the cleared flag when patient changes
        this.hasClearedZ00Codes = false;
        // if(!this.patient.medicalSchemeCode || ){

        // }
        if (this.patient) {
          this.patchForm();
          //this.fetchPatientMedicalInfo() //Fetch and set patient medical aid info 
          this.cdr.detectChanges();
        }
        else {
          this.resetForm();
        }
      });

    this.setAddType();

    // Subscribe to detected commands from speech recognition
    this.subscriptions.push(
      this.speechRecognitionService.getDetectedCommand().subscribe((commandData) => {
        this.handleVoiceCommand(commandData.command, commandData.payload);
      })
    );
  }

  fetchInitialData(): Observable<any> {
    // Simulate an asynchronous operation (e.g., fetching data)
    return of(null).pipe(delay(100)); // Adjust delay as needed
  }

  /* setupValueChangesListeners() {
    // Use valueChanges for dynamic search
    this.filteredSchemes = this.form.get('medicalSchemeCode')!.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value) => {
        if (typeof value === 'string') {
          return value.trim().length >= 3 ? this.getSchemesList(value) : of([]);
        } else if (value && typeof value === 'object' && value.name) {
          // If object, return a list with that scheme for UI consistency
          return of([value]);
        } else {
          return of([]);
        }
      })
    );


    this.filteredPlans = this.form.get('medicalPlanCode')!.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map(value => typeof value === 'string' ? value : value?.option || ''),
      map(name => name ? this._filterPlans(name) : this.plans)
    );

    this.filteredPlans = this.form.get('medicalPlanOptionCode')!.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map(value => typeof value === 'string' ? value : value?.option || ''),
      map(name => name ? this._filterPlans(name) : this.plans)
    );
  } */

  /** Function to display a scheme in the autocomplete input */
  displayScheme(scheme: any): string {
    return typeof scheme === 'object' && scheme?.name ? scheme.name : '';
  }

  /** Function to display a plan in the autocomplete input */
  displayPlan(plan: Plan): string {
    return plan ? plan.option : '';
  }
  /** Function to display a plan in the autocomplete input */
  displayOptionPlan(planOption: PlanOption): string {
    return planOption ? planOption.option : '';
  }

  /** Helper to filter plans based on input */
  private _filterPlans(value: string): Plan[] {
    const filterValue = value.toLowerCase();
    return this.plans.filter(plan => plan.option.toLowerCase().includes(filterValue));
  }

  /** Handle Scheme Selection */
  /*  onSchemeSelected(scheme: Scheme): void {
     this.selectedScheme = scheme;
     this.form.patchValue({ medicalSchemeCode: this.selectedScheme })
     console.log('On selected scheme: ', this.form.get('medicalSchemeCode')?.value)
     this.form.patchValue({ medicalAid: this.selectedScheme.name })
 
     this.getSchemeDetails(this.selectedScheme.code).subscribe(response => {
 
 
       this.plans = response.plans || [];
       this.filteredPlans = of(this.plans);
       this.form.get('planControl')?.setValue('');
 
     });
     // Ensure UI updates
     this.cdr.detectChanges();
   } */

  /** Handle Plan Selection */
  /* onPlanSelected(plan: Plan): void {
    this.selectedPlan = plan;
    this.form.patchValue({ medicalPlanCode: this.selectedPlan });
    this.form.patchValue({ medicalAidPlan: this.selectedPlan.option })
    console.log('On selected plan:', this.form.get('medicalAidPlan')?.value)

    this.getPlanOptions(plan.code).subscribe(options => {
      this.filteredPlanOptions = of(options);
    });

    // Ensure UI updates
    this.cdr.detectChanges();
  } */

  /** Handle Plan Option Selection */
  /* onPlanOptionSelected(planOption: PlanOption): void {
    this.selectedPlanOption = planOption;
    this.form.patchValue({ medicalPlanOptionCode: this.selectedPlanOption });

    console.log('On selected option: ', this.selectedPlanOption)
    // Ensure UI updates
    this.cdr.detectChanges();
  } */

  /** Fetch Schemes (Fixed API Call) */
  /* getSchemesList(search: string): Observable<any> {

    return this.http.post<any>(`${environment.API_URL}/api/medprax/schemes/by-name`, { schemeName: search }).pipe(
      map(response => response.schemeList || []),
      catchError(error => {
        console.error("Failed to fetch schemes:", error.message);
        return of([]);
      })
    );
  }

  private mapToScheme(responseScheme: any): Scheme {
    return {
      name: responseScheme.name,
      code: responseScheme.code,
      schemeAdministratorCode: responseScheme.schemeAdministratorCode,
      managedCareCompanyCode: responseScheme.managedCareCompanyCode,
      captitionScheme: responseScheme.captitionScheme,
      discontinuedDate: responseScheme.discontinuedDate,
      cmsReferenceNumber: responseScheme.cmsReferenceNumber,
      category: responseScheme.category,
      schemeTelephoneNumber: responseScheme.schemeTelephoneNumber,
      schemeEmail: responseScheme.schemeEmail,
      schemeUrl: responseScheme.schemeUrl,
    };
  } */

  /* fetchPatientMedicalInfo() {

    if (!this.patient?.medicalSchemeCode) return; //Ensure the patient has a scheme

    this.patientService.getPatientMedicalInfo(
      this.patient.medicalSchemeCode,
      this.patient.medicalPlanCode,
      this.patient.medicalPlanOptionCode,
    ).subscribe({
      next: (response) => {
        if (response) {
          this.selectedScheme = this.mapToScheme(response.scheme || null);

          this.selectedPlan = response.plan || null;
          this.selectedPlanOption = response.planOption || null;

          this.filteredSchemes = of([this.selectedScheme!])
          // this.form.patchValue({ medicalSchemeCode: this.selectedScheme })

          setTimeout(() => {
            this.form.patchValue({
              medicalSchemeCode: this.selectedScheme,
              medicalPlanCode: this.selectedPlan,
              medicalPlanOptionCode: this.selectedPlanOption,
            })
          }, 0);

          if (!this.selectedScheme) {
            setTimeout(() => {
              console.log('Dialog open 3')
              this.dialog.open(this.medicalAidInformation!, { width: '400px', disableClose: true });
            }, 0)
          }
          this.cdr.detectChanges();

        }
      },
      error: (error) => {
        console.error("Error fetching medical aid info: ", error.message)
      },
      complete: () => {
        console.log("Medical info fetch completed")
      }
    })
  } */

  getSchemeDetails(schemeCode: string): Observable<any> {
    return this.http.get<any>(`${environment.API_URL}/api/scheme/plan/${schemeCode}`).pipe(
      map(response => {
        if (!response || !response.plans) {
          throw new Error('Invalid API response: plans not found');
        }
        return response;
      }),
      catchError(error => {
        console.error("Failed to fetch scheme details:", error.message);
        return of({ plans: [] }); // Return an empty array if there's an error
      })
    );
  }

  /** Fetch Plan Options (Fixed API Call) */
  getPlanOptions(planCode: string): Observable<any> {
    return this.http.get<any>(`${environment.API_URL}/api/scheme/plan/options/${planCode}`).pipe(
      map(response => response.planOptions || []),
      catchError(error => {
        console.error("Failed to fetch plan options:", error.message);
        return of([]);
      })
    );
  }

  override ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    // Clear admission sheet files to prevent memory leaks
    this.admissionSheetFileObjects = [];
  }

  private handleVoiceCommand(command: string, payload?: any): void {
    switch (command) {
      case 'CLOSE_FILE':
        console.log('Closing patient details page...');
        this.returnToParent();
        break;

    }
  }

  resetForm() {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        this.patient = null;
        this.selectedIcdCodes = [];
        this.visibleDiagnosisFields = [];
        this.selectedAllergies = [];
        this.selectedMedicalHistory = [];
        this.selectedMedicationHistory = [];
        this.selectedMedicalTeam = [];
        this.patientAuthNumbers = [];
        this.form.reset();
        this.initForm();
        this.medicalAidChanges = null;
        this.medicalAidInitial = null;
        this.setupStatusChangeListener();
        this.generateUniquePatientNumber();
        // Clear admission sheet files when resetting form
        this.admissionSheetFileObjects = [];
        resolve();
      });
    })

  }

  /** A state unit (tasks/state-mode.md) invites nobody: patients are added by hand. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  /** The patient type to start an add with: an invite becomes an outpatient for a state unit. */
  addStatusFor(type: string): string {
    return this.stateMode && type === 'invite' ? 'opd' : type;
  }

  private setAddType() {
    this.route.queryParams.pipe(
      takeUntil(this.destroy$),
    ).subscribe(async params => {
      const type = params['type'];
      const quick = params['quick'];
      this.isQuickAdd = quick === 'true';
      this.hasQuickAddParam = quick === 'true';
      this.redirectTo = params['redirectTo'];
      this.redirectDate = params['date'];
      this.redirectTimeSlot = params['timeSlot'];
      this.redirectPhysicianId = params['physicianId'];
      this.scrollPositionService.scrollTo('top', true);
      if (type) {
        await this.resetForm();
        this.form.patchValue({ status: this.addStatusFor(type) });
      }

    });
  }




  patchForm() {
    if (!this.patient) {
      return; // Guard clause in case this.patient is not yet set
    }

    // Basic patching for directly matched fields
    this.form.patchValue(this.patient);
    this.medicalAidChanges = null;
    this.medicalAidInitial = {
      schemeCode: schemeCodeOf(this.patient.medicalSchemeCode),
      planCode: schemeCodeOf(this.patient.medicalPlanCode),
      optionCode: schemeCodeOf(this.patient.medicalPlanOptionCode),
      medicalAid: this.patient.medicalAid,
      medicalAidPlan: this.patient.medicalAidPlan,
    };

    // If hospitalId is available, select the hospital to load its wards
    if (this.patient.hospitalId) {
      const selectedHospital = this.hospitals?.find(hospital => hospital.id === this.patient.hospitalId);
      if (selectedHospital?.wards) {
        this.wards = _.orderBy(selectedHospital.wards, ['name'], ['asc']);
      }
    }

    // For any other special handling or transformations, continue below
    if (this.patient.admissionDate) {
      this.form.patchValue({ admissionDate: moment.utc(this.patient.admissionDate, 'DD/MM/YYYY') });
    }

    if (this.patient.dob) {
      const dob = this.patient.dob;
      const dobMoment = moment.utc(dob, 'DD/MM/YYYY', true).isValid()
        ? moment.utc(dob, 'DD/MM/YYYY')
        : moment.utc(dob);
      this.form.patchValue({ dob: dobMoment });
    }
    if (this.patient.medicalDateJoined) {
      this.form.patchValue({ medicalDateJoined: moment.utc(this.patient.medicalDateJoined, 'DD/MM/YYYY') });
    }

    if (this.patient.icdCodes) {
      this.onIcdCodesChanged(this.patient.icdCodes);
    }

    if (this.patient.allergies) {
      this.onAllergiesChanged(this.patient.allergies);
    }

    if (this.patient.medicalHistory) {
      this.onMedicalHistoryChanged(this.patient.medicalHistory);
    }

    if (this.patient.medicationHistory) {
      this.onMedicationHistoryChanged(this.patient.medicationHistory);
    }

    if (this.patient.medicalTeam) {
      this.onMedicalTeamChanged(this.patient.medicalTeam);
    }

    if (this.patient.authorizationNumbers) {
      this.onAuthListChanged(this.patient.authorizationNumbers);
    }

    const data = this.patient;
    // Patch the diagnosis fields dynamically
    for (let i = 2; i <= 24; i++) {
      const fieldName = `diagnosis${i}`;
      if (data[fieldName]) {
        this.addDiagnosisField();
      }
    }

    this.configureFormValidation(this.form.controls);

    if (!this.patient.patientNumber) {
      this.generateUniquePatientNumber();
    }

    // Clear Z00 codes and UNSPECIFIED diagnosis if patient requires ICD validation, we haven't cleared them yet, and user is a doctor
    if (this.patient.requiresICDValidation && !this.hasClearedZ00Codes && this.user?.userType === 'doctor') {
      this.clearZ00CodesAndUnspecifiedDiagnosis();
    }

    /* if (this.patient.medicalSchemeCode) {
      of(this.patient.medicalSchemeCode)
        .pipe(
          takeUntil(this.destroy$),
          // Step 1: Get schemes list based on name
          switchMap(schemeCode =>
            this.getSchemesList(schemeCode.name).pipe(
              // Step 2: Find the matching scheme
              switchMap(schemes => {
                console.log('schemes: ', schemes)
                const matchingScheme = schemes.find((scheme: Scheme) => scheme.code === schemeCode.code);
                console.log('matchingScheme: ', matchingScheme)
                if (!matchingScheme) return of(null);
                this.selectedScheme = matchingScheme;
                this.form.patchValue({
                  medicalSchemeCode: matchingScheme,
                });
                // Step 3: If medicalPlanCode exists, get plan details
                if (this.patient.medicalPlanCode) {
                  return this.getSchemeDetails(matchingScheme.code).pipe(
                    switchMap(response => {
                      this.plans = response.plans || [];
                      console.log('plans: ', this.plans)
                      const matchingPlan = this.plans.find(plan => plan.code === this.patient.medicalPlanCode.code);
                      console.log('matchingPlan: ', matchingPlan)
                      if (!matchingPlan) return of(null);
                      this.selectedPlan = matchingPlan;
                      this.form.patchValue({
                        medicalPlanCode: matchingPlan,
                      });

                      // Step 4: If medicalPlanOptionCode exists, get plan options
                      if (this.patient.medicalPlanOptionCode) {
                        return this.getPlanOptions(matchingPlan.code).pipe(
                          tap(options => {
                            console.log('options: ', options)
                            console.log('medicalPlanOptionCode: ', this.patient.medicalPlanOptionCode)
                            const matchingOption = options.find((opt: PlanOption) => opt.code === this.patient.medicalPlanOptionCode.code);
                            console.log('matchingOption: ', matchingOption)
                            if (matchingOption) {
                              this.selectedPlanOption = matchingOption;
                              this.form.patchValue({ medicalPlanOptionCode: matchingOption });
                            }
                          })
                        );
                      }

                      return of(null);
                    })
                  );
                }

                return of(null);
              })
            )
          )
        ).subscribe();
    } */

  }

  private generateUniquePatientNumber(): void {
    if (!this.groupService.group.generatePatientNumbers) return;
    const timestamp = Date.now().toString();
    const seed = this.groupId + timestamp;
    const hash = this.hashString(seed);
    const identifier = hash.slice(0, 6).toUpperCase(); // Return the first 6 characters of the hash
    this.form.patchValue({ patientNumber: identifier });
  }

  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // Convert to 32bit integer
    }
    return hash.toString(36).replace('-', ''); // Convert to base36 and remove any negative sign
  }


  selectHospital(event: MatSelectChange) {
    const selectedHospitalId = event.value;
    // Find the hospital object from the cached this.hospitals array
    const selectedHospital = this.hospitals.find(hospital => hospital.id === selectedHospitalId);

    if (selectedHospital && selectedHospital.wards) {
      // Sort wards alphabetically using Lodash
      this.wards = _.orderBy(selectedHospital.wards, ['name'], ['asc']);
    } else {
      // Reset wards if no hospital is found or the hospital has no wards
      this.wards = [];
    }
  }

  initForm() {
    this.form = this.fb.group({
      inviteId: [''],
      groupId: [patientGroupId(this.patient, this.groupId), Validators.required],
      hospitalId: [null, Validators.required],
      wardId: [null, Validators.required],
      status: ['opd', Validators.required],
      patientIdentifier: ['', Validators.required],
      title: [''],
      initials: [''],
      name: ['', Validators.required],
      surname: ['', Validators.required],
      patientNumber: ['', Validators.required],
      dob: ['', Validators.required],
      gender: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      idType: ['id'],
      idNumber: ['', SaIdValidator.isValidSaId()],
      passportNumber: [''],
      passportCountry: [''],
      phoneNumber: [''],
      mobileNumber: ['', [Validators.required, phoneNumberValidator]],
      address: [''],
      addressCode: [''],
      medicalAid: [''],
      medicalAidNumber: [''],
      medicalAidPlan: [''],
      medicalMainMember: [''],
      medicalDependantCode: [''],
      medicalDateJoined: [''],
      gapCover: [''],
      gapCoverNumber: [''],
      country: [''],
      religion: [''],
      language: [''],
      occupation: [''],
      employer: [''],
      workPhone: [''],
      workAddress: [''],
      workAddressCode: [''],
      maritalStatus: [''],
      diagnosis: ['', Validators.required],
      admissionDate: ['', Validators.required],
      admissionType: ['', Validators.required],
      admissionSummary: [''],
      bed: [''],
      authorizationNumbers: [[]],
      emergencyContact: [''],
      emergencyContactRelationship: [''],
      emergencyContactMobile: [''],
      emergencyContactPhone: [''],
      emergencyContactWork: [''],
      emergencyContactEmail: ['', Validators.email],
      emergencyContactAddress: [''],
      emergencyContactAddressCode: [''],
      emergencyContact2: [''],
      emergencyContactRelationship2: [''],
      emergencyContactMobile2: [''],
      emergencyContactPhone2: [''],
      emergencyContactWork2: [''],
      emergencyContactEmail2: ['', Validators.email],
      emergencyContactAddress2: [''],
      emergencyContactAddressCode2: [''],
      liableTitle: [''],
      liableInitials: [''],
      liableName: [''],
      liableSurname: [''],
      liableIdType: ['id'],
      liableIdNumber: ['', SaIdValidator.isValidSaId()],
      liablePassportNumber: [''],
      liablePassportCountry: [''],
      liableEmail: ['', Validators.email],
      liableMobileNumber: [''],
      liablePhoneNumber: [''],
      liableAddress: [''],
      liableAddressCode: [''],
      liableOccupation: [''],
      liableEmployer: [''],
      liableWorkPhone: [''],
      liableWorkAddress: [''],
      liableWorkAddressCode: [''],
      icdCodes: [[]],
      medicalTeam: [[]],
      allergies: [[]],
      noAllergies: [false],
      medicalHistory: [[]],
      noMedicalHistory: [false],
      medicationHistory: [[]],
      noMedicationHistory: [false],
      physicianId: [''],
      weight: [''],
      height: [''],
      requiresValidation: [false],
      reasonForVisiting: [''],
      // HIDDEN FINANCIAL
      //medicalSchemeCode: [''],
      //medicalPlanCode: [''],
      //medicalPlanOptionCode: [''],

    });

    // Add diagnosis fields dynamically
    for (let i = 2; i <= 24; i++) {
      const fieldName = `diagnosis${i}`;
      this.form.addControl(fieldName, this.fb.control(''));
    }

    // Listen to changes in the email input and check for typos
    this.emailSuggestion = null;
    this.form.get('email')?.valueChanges.subscribe((value: string) => {
      this.emailSuggestion = checkEmailTypo(value);
      this.updateContactValidation();
    });

    // Listen to changes in mobile number to update validation
    this.form.get('mobileNumber')?.valueChanges.subscribe(() => {
      this.updateContactValidation();
    });

    this.configureFormValidation(this.form.controls);
  }

  get status() {
    return this.form.get('status')?.value;
  }


  addDiagnosisField(): void {
    const nextIndex = this.visibleDiagnosisFields.length + 2; // Start at 2 for diagnosis2
    if (nextIndex <= 24) {
      const fieldName = `diagnosis${nextIndex}`;
      this.form.addControl(fieldName, this.fb.control(''));
      this.visibleDiagnosisFields.push(fieldName);
    }
  }

  onIDTypeChange(event: MatSelectChange, isLiableId?: Boolean) {
    switch (event.value) {
      case 'id':
        isLiableId ? this.form.patchValue({ liablePassportNumber: '', liablePassportCountry: '' }) : this.form.patchValue({ passportNumber: '', passportCountry: '' });
        break;
      case 'passport':
        isLiableId ? this.form.patchValue({ liableIdNumber: '', liablePassportCountry: '' }) : this.form.patchValue({ idNumber: '', passportCountry: '' });
        break;
    }
  }


  onIcdCodesChanged(codes: IcdCode[]) {
    this.selectedIcdCodes = codes;
    this.form.patchValue({ icdCodes: codes });
  }

  onMedicalTeamChanged(medicalTeam: any[]) {
    this.selectedMedicalTeam = medicalTeam;
    this.form.patchValue({ medicalTeam: medicalTeam });
  }

  onAllergiesChanged(allergies: any[]) {
    this.selectedAllergies = allergies;
    this.form.patchValue({ allergies: allergies });
  }

  onMedicalHistoryChanged(history: any[]) {
    this.selectedMedicalHistory = history;
    this.form.patchValue({ medicalHistory: history });
  }

  onMedicationHistoryChanged(history: any[]) {
    this.selectedMedicationHistory = history;
    this.form.patchValue({ medicationHistory: history });
  }

  onNoAllergiesChanged(noAllergies: boolean) {
    this.form.patchValue({ noAllergies: noAllergies });
  }

  onNoMedicalHistoryChanged(noMedicalHistory: boolean) {
    this.form.patchValue({ noMedicalHistory: noMedicalHistory });
  }

  onNoMedicationHistoryChanged(noMedicationHistory: boolean) {
    this.form.patchValue({ noMedicationHistory: noMedicationHistory });
  }

  /** A number stored on the patient record was removed from the authorisation list. */
  onLegacyAuthorizationRemoved(entry: { authNumber: string }) {
    const remaining = (this.patientAuthNumbers || []).filter(auth => auth?.authNumber !== entry.authNumber);
    this.onAuthListChanged(remaining);
    this.form.markAsDirty();
  }

  onAuthListChanged(authorizationNumbers: any[]) {
    const sortedAuth = _.sortBy(authorizationNumbers, (auth) => moment(auth.date)).reverse() || [];
    this.patientAuthNumbers = sortedAuth;
    this.form.patchValue({ authorizationNumbers: sortedAuth });
  }

  setupStatusChangeListener() {

    const name$ = this.form.get('name')!.valueChanges.pipe(startWith(this.form.get('name')!.value));
    const surname$ = this.form.get('surname')!.valueChanges.pipe(startWith(this.form.get('surname')!.value));
    const dob$ = this.form.get('dob')!.valueChanges.pipe(startWith(this.form.get('dob')!.value));
    const gender$ = this.form.get('gender')!.valueChanges.pipe(startWith(this.form.get('gender')!.value));

    combineLatest([name$, surname$, dob$, gender$]).pipe(
      map(([name, surname, dob, gender]) => {
        // Convert dob to the required format if it exists, otherwise use an empty string
        const formattedDob = dob ? moment(dob, 'DD/MM/YYYY').format('DDMMYYYY') : '';
        return this.generateIdentifier({ name, surname, dob: formattedDob, gender });
      }),
      takeUntil(this.destroy$)
    ).subscribe(identifier => {
      // Update the patientIdentifier field without emitting a new event to prevent an infinite loop
      this.form.get('patientIdentifier')!.setValue(identifier, { emitEvent: false });
    });

    this.form.get('status')!.valueChanges
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.configureFormValidation(this.form.controls);
      });

  }

  generateIdentifier(patient: any): string {
    const { name, surname, dob, gender } = patient;

    const uppercaseName = name ? name.toUpperCase() : '';
    const uppercaseSurname = surname ? surname.toUpperCase() : '';

    // Remove '/' from dob and adjust for the format DD/MM/YYYY
    const dobString = dob ? dob.replace(/\//g, '') : '';
    const genderLetter = gender ? gender.charAt(0).toUpperCase() : '';

    return `${uppercaseName.charAt(0)}${uppercaseSurname.charAt(0)}${dobString}${genderLetter}`;
  }


  configureFormValidation(controls: { [key: string]: AbstractControl; }) {

    const status = controls['status'].value;

    switch (status) {
      case 'inpatient':
        controls['physicianId'].clearValidators();
        // For inpatients, these fields are required
        controls['admissionDate'].setValidators([Validators.required]);
        controls['admissionType'].setValidators([Validators.required]);
        controls['hospitalId'].setValidators([Validators.required]);
        controls['wardId'].setValidators([Validators.required]);
        controls['patientNumber'].setValidators([Validators.required]);
        controls['diagnosis'].setValidators([Validators.required]);
        controls['gender'].setValidators([Validators.required]);
        controls['dob'].setValidators([Validators.required]);
        break;
      case 'opd':
        // For opd patients, clear validators
        controls['admissionDate'].clearValidators();
        controls['admissionType'].clearValidators();
        controls['hospitalId'].clearValidators();
        controls['wardId'].clearValidators();
        controls['physicianId'].clearValidators();
        controls['patientNumber'].setValidators([Validators.required]);
        controls['diagnosis'].setValidators([Validators.required]);
        controls['gender'].setValidators([Validators.required]);
        controls['dob'].setValidators([Validators.required]);
        break;
      case 'invite':
        // For invite patients, clear validators
        controls['admissionDate'].clearValidators();
        controls['admissionType'].clearValidators();
        controls['hospitalId'].clearValidators();
        controls['wardId'].clearValidators();
        controls['patientNumber'].clearValidators();
        controls['diagnosis'].clearValidators();
        controls['gender'].clearValidators();
        controls['dob'].clearValidators();
        //controls['email'].setValidators([Validators.email]);
        controls['physicianId'].setValidators([Validators.required]);
        break;
    }

    const currentUser = this.userService.user;
    const groupUsers = this.groupUsers || this.groupService.groupUsers;
    const groupOwner = this.groupService.getGroupOwner();

    if (groupUsers.findIndex(gu => gu.userId === currentUser.id) > -1) {
      this.form.patchValue({ physicianId: currentUser.id });
    } else if (groupOwner) {
      this.form.patchValue({ physicianId: groupOwner.userId });
    } else {
      this.form.patchValue({ physicianId: groupUsers[0].userId });
    }

    if (status === 'invite') {
      this.isQuickAdd = false;
    } else if (this.hasQuickAddParam) {
      this.isQuickAdd = true;
    }

    if (this.isQuickAdd) {
      controls['gender'].clearValidators();
      controls['diagnosis'].clearValidators();

      if (status === 'inpatient') {
        controls['admissionDate'].patchValue(moment().utc());
        controls['admissionType'].patchValue('Emergency Admission');
        if (this.hospitals.length === 1) {
          controls['hospitalId'].patchValue(this.hospitals[0].id);
          this.selectHospital({ value: this.hospitals[0].id } as MatSelectChange);
        }
      }
    } else if (status !== 'invite') {
      controls['gender'].setValidators([Validators.required]);
      controls['diagnosis'].setValidators([Validators.required]);
      /*  if (status === 'inpatient') {
         controls['admissionDate'].patchValue('');
         controls['admissionType'].patchValue('');
         controls['hospitalId'].patchValue('');
       } */
    }

    // Re-evaluate the validation status of the controls
    controls['admissionDate'].updateValueAndValidity();
    controls['admissionType'].updateValueAndValidity();
    controls['hospitalId'].updateValueAndValidity();
    controls['wardId'].updateValueAndValidity();
    controls['diagnosis'].updateValueAndValidity();
    controls['gender'].updateValueAndValidity();
    controls['dob'].updateValueAndValidity();
    //controls['email'].updateValueAndValidity();
    controls['physicianId'].updateValueAndValidity();
    this.form.updateValueAndValidity();

    this.updateContactValidation();

    FormUtils.showAllFormErrorStates(this.form);

  }
  toggleNoDetailsAvailable(event: MatCheckboxChange) {
    this.noContactDetails = event.checked;
    this.updateContactValidation();
  }



  private updateContactValidation() {
    const emailControl = this.form.get('email');
    const mobileControl = this.form.get('mobileNumber');

    if (!emailControl || !mobileControl) return;

    if (this.noContactDetails) {
      // Remove validation from both fields when "no details available" is checked
      emailControl.clearValidators();
      mobileControl.clearValidators();
    } else {
      const email = emailControl.value;
      const mobile = mobileControl.value;

      // If email has a value, remove required from mobile
      if (email) {
        emailControl.setValidators([Validators.email]);
        mobileControl.clearValidators();
      }
      // If mobile has a value, remove required from email
      else if (mobile) {
        emailControl.clearValidators();
        mobileControl.clearValidators();
      }
      // If neither has a value, both are required
      else {
        emailControl.setValidators([Validators.required, Validators.email]);
        mobileControl.setValidators([Validators.required]);
      }
    }

    // Update validity without triggering valueChanges events
    emailControl.updateValueAndValidity({ emitEvent: false });
    mobileControl.updateValueAndValidity({ emitEvent: false });
  }

  onMedicalAidPick(pick: MedicalAidPick | null) {
    this.medicalAidChanges = pick ? medicalAidFields(pick) : null;
    if (this.medicalAidChanges) {
      this.form.patchValue({ medicalAid: this.medicalAidChanges.medicalAid, medicalAidPlan: this.medicalAidChanges.medicalAidPlan });
    }
  }

  async savePatient() {
    if (this.isQuickAdd) {
      this.form.patchValue({ requiresValidation: true });
    } else {
      this.form.patchValue({ requiresValidation: false });
    }

    if (this.stateMode && this.form.value.status === 'invite') this.form.patchValue({ status: 'opd' });
    const { icdCodes, status } = this.form.value;

    if (status === 'invite' && !this.isPremium && this.textMessageBalance === 0 && !this.form.get('email')?.value) {
      this.showToastMessage('You do not have any text message credits available to send this invite. Please enter an email address or recharge your text message credits to continue.');
      return;
    }
    if (status !== 'invite' && !this.isQuickAdd && !icdCodes.length) {
      this.showToastMessage('At least one ICD-10 code is required. Please add an ICD-10 code to continue.');
      return;
    }

    //link patient medical aid toast

    const validationMessage = this.validationService.validateForm(this.form);
    if (validationMessage) {
      this.showConfirmationDialog('VALIDATION ERROR', validationMessage, [], 'CLOSE', 'OK', true);

      return;
    }

    this.isLoading = true;
    // Check if this.patient.id exists to determine if we're updating or adding a new patient
    if (this.patient && this.patient.id) {
      const { admissionDate } = this.form.value;
      const previousAdmissionDate = moment.utc(this.patient.admissionDate, 'DD/MM/YYYY').startOf('day');
      const currentAdmissionDate = moment.utc(admissionDate).startOf('day');

      if (currentAdmissionDate.isAfter(previousAdmissionDate, 'day')) {
        await this.patientBillingService.checkOverCoding({
          startDate: previousAdmissionDate.clone().format('YYYY-MM-DD'),
          endDate: currentAdmissionDate.clone().format('YYYY-MM-DD')
        }, this.hospitals, this.patient, this.patientProcedureCodes, this);
      } else if (currentAdmissionDate.isBefore(previousAdmissionDate, 'day')) {
        await this.patientBillingService.checkMissingCoding(this.patient, this.form.value, this.hospitals, this.patientProcedureCodes, this);
      }
      // Update existing patient
      this.patientService.updatePatient(this.patient.id, { ...this.form.value, ...this.medicalAidChanges })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async (result) => {
            this.showToastMessage('Patient updated successfully');
            this.loadPatient(result.data.id);
            this.isLoading = false;
          },
          error: (error) => {
            console.error('Error updating patient: ', error);
            this.showToastMessage('There was an error updating the patient. Please try again.');
            this.isLoading = false;
          }
        });
    } else {
      // Add new patient, including the scheme, plan, planOption
      /*
       * §30. The attachment picker's selection rides the creation payload.
       *
       * ABSENT vs EMPTY is load-bearing on the server: `undefined` means no picker was involved
       * and the practice's `sendWithInvite` configuration decides, while `[]` means the user
       * unticked everything. So it is sent ONLY for an invite, which is the only status whose
       * panel renders the picker — an inpatient payload carrying `[]` would tell the server the
       * user unticked everything on a screen that never offered them anything.
       */
      const newPatientData = this.form.value.status === 'invite'
        ? { ...this.form.value, practiceDocumentIds: this.selectedDocumentIds }
        : { ...this.form.value, ...this.medicalAidChanges };
      this.patientService.findSimilarPatients(newPatientData)
        .pipe(
          takeUntil(this.destroy$),
          switchMap(similarPatients => {
            if (similarPatients.length > 0) {
              // Show dialog with similar patients and get user confirmation
              return this.showConfirmationDialog('Similar Patients Found', 'There are similar patients in the system. Do you want to proceed with adding a new patient?', similarPatients, 'NO', 'YES', false, 5)
                .then(confirmed => {
                  if (confirmed) {
                    return this.patientService.addPatient(newPatientData).toPromise();
                  } else {
                    return { reviewSimilarPatients: true };
                  }
                });
            } else {
              return this.patientService.addPatient(newPatientData).toPromise();
            }
          }),
        )
        .subscribe({
          next: async (result: any) => {
            if (result.reviewSimilarPatients) {
              this.isLoading = false;
              return; // Exit early
            }

            await this.patientBillingService.checkMissingCoding(result.data, this.form.value, this.hospitals, [], this);

            // Create a patient note with the admission sheet image if available
            if (this.admissionSheetFileObjects.length > 0) {
              await this.createAdmissionSheetNote(result.data);
            }
            this.usage.flow('new patient', 'done');
            this.isLoading = false;
            this.showToastMessage('Patient saved successfully');
            if (this.redirectTo) {
              this.handleRedirection(result.data.id);
            } else {
              this.loadPatient(result.data.id);
            }
          },
          error: (error) => {
            console.error('Error saving patient: ', error);
            this.showToastMessage('There was an error saving the patient. Please try again.');
            this.isLoading = false;
          }
        });
    }
  }

  async deletePatient() {
    const confirmed = await this.showConfirmationDialog();
    if (!confirmed) return;

    this.patientService.deletePatient(this.patient.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (result) => {
          await this.promptFileCleanup();
          this.showToastMessage('Patient deleted successfully');
          this.loadPatient(null);
        },
        error: (error) => {
          console.error('Error saving patient: ', error);
          this.showToastMessage('There was an error deleting the patient. Please try again.');
        }
      });
  }

  private async promptFileCleanup(): Promise<boolean> {
    const confirmed = await this.showConfirmationDialog('Remove Associated Information', 'Would you like to remove any appointments, coding, procedures, and notes associated with this patient?', [], 'NO', 'YES', false, 5);
    if (!confirmed) return false;

    this.patientService.deleteAssociatedData(this.patient.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (result) => {
          this.showToastMessage('Associated information deleted successfully');
          return true;
        },
        error: (error) => {
          console.error('Error removing associated data patient: ', error);
          this.showToastMessage('There was an error deleting the associated patient information.');
          return false;
        }
      });

    return false;
  }



  loadPatient(patientId: string | null) {
    this.patientService.getPatientById(patientId, true)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(
        result => {
          if (!patientId) {
            this.router.navigate(['/patients', {
              outlets: {
                patient: null,
              }
            }]);
            return;

          }
          this.router.navigate(['/patients', {
            outlets: {
              patient: ['file', patientId],
            }
          }]);
        }, error => {
          console.error('Error loading patient: ', error);
          this.showToastMessage('There was an error loading the patient. Please try again.');
        });
  }


  handleRedirection(patientId: string | null) {
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(
        result => {
          this.router.navigate(['/patients', {
            outlets: {
              patient: [this.redirectTo]
            }
          }], {
            queryParams: {
              patientId: patientId,
              date: this.redirectDate,
              timeSlot: this.redirectTimeSlot,
              physicianId: this.redirectPhysicianId
            }
          });
        }, error => {
          console.error('Error loading patient: ', error);
          this.showToastMessage('There was an error loading the patient. Please try again.');
        });


  }

  copyPatientDetails(event: MatCheckboxChange) {

    const values = this.form.value;
    let data = {
      liableTitle: '',
      liableName: '',
      liableInitials: '',
      liableSurname: '',
      liableIdType: '',
      liableIdNumber: '',
      liablePassportNumber: '',
      liablePassportCountry: '',
      liableEmail: '',
      liableMobileNumber: '',
      liablePhoneNumber: '',
      liableAddress: '',
      liableAddressCode: '',
      liableOccupation: '',
      liableEmployer: '',
      liableWorkPhone: '',
      liableWorkAddress: '',
      liableWorkAddressCode: '',
    }

    if (!event.checked) {
      this.form.patchValue(data);
      return;
    }

    data = {
      liableTitle: values.title,
      liableName: values.name,
      liableInitials: values.initials,
      liableSurname: values.surname,
      liableIdType: values.idType,
      liableIdNumber: values.idNumber,
      liablePassportNumber: values.passportNumber,
      liablePassportCountry: values.passportCountry,
      liableEmail: values.email,
      liableMobileNumber: values.mobileNumber,
      liablePhoneNumber: values.phoneNumber,
      liableAddress: values.address,
      liableAddressCode: values.addressCode,
      liableOccupation: values.occupation,
      liableEmployer: values.employer,
      liableWorkPhone: values.workPhone,
      liableWorkAddress: values.workAddress,
      liableWorkAddressCode: values.workAddressCode,
    }

    this.form.patchValue(data);

  }


  async returnToParent(showConfirmation: boolean = true) {
    if (showConfirmation) {
      const confirmed = await this.showConfirmationDialog('CONFIRM', 'Are you sure you want to leave this page? All unsaved changes will be lost.');
      if (!confirmed) return;
    }
    if (this.patient && this.patient.id) {
      this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patient.id}`] } }]);
    } else {

      this.router.navigate(['/patients', { outlets: { patient: null } }]);
    }
  }


  admissionSheetFileObjects: FileObject[] = [];
  admissionSheetUrl: string = '';
  admissionProcessing: boolean = false;

  openAddUsingImageDialog() {
    // Always false if premium, otherwise check token balance
    if (this.isPremium) {
      this.lockProcessing = false;
    } else if (this.usageService.tokenBalanceSnapshot < 0) {
      this.lockProcessing = true;
    } else {
      this.lockProcessing = false;
    }
    this.dialog.open(this.scanPatientDetailsDialog!, ADMISSION_SHEET_DIALOG_CONFIG);
  }

  openMedicalAidInformationDialog() {
    this.dialog.closeAll()
  }

  private resetAdmissionScanning() {
    this.admissionSheetFileObjects = [];
    this.admissionSheetUrl = '';
    this.admissionProcessing = false;
  }

  onAdmissionSheetUpload(files: FileObject[]) {
    this.resetAdmissionScanning();

    if (files.length > 0) {
      // Keep the file object as-is: its `type` is the real MIME type and is what the
      // saved copy's file extension is derived from further down.
      this.admissionSheetFileObjects = [files[0]];
      this.uploadImage();
    }
  }

  onAdmissionSheetDelete(fileObject: FileObject) {
    this.resetAdmissionScanning();
  }

  /** Pre-picks the scanned medical aid in the picker when the scheme list has it; otherwise the text stays as read. */
  private async linkScannedMedicalAid() {
    const scanned = this.patient;
    const resolved = await resolveScannedMedicalAid(this.schemeReference.schemes, scanned, async (request) =>
      (await firstValueFrom(this.aiService.disambiguateScheme(this.groupId, request))).bestId, this.schemeReference.learned);
    if (!resolved || this.patient !== scanned) return;
    this.medicalAidChanges = medicalAidFields(resolved);
    this.medicalAidInitial = { schemeCode: resolved.scheme.code, planCode: resolved.option.planCode, optionCode: resolved.option.optionCode };
    this.form.patchValue({ medicalAid: this.medicalAidChanges.medicalAid, medicalAidPlan: this.medicalAidChanges.medicalAidPlan });
    this.cdr.detectChanges();
  }

  async uploadImage() {
    this.analyticsService.trackButtonClick(`Patient Details Image Extraction`);
    this.admissionProcessing = true;
    const formData = new FormData();
    if (this.admissionSheetFileObjects.length > 0) {
      formData.append('image', await this.blobToFile(this.admissionSheetFileObjects[0].url, `${moment().valueOf()}`));
    }

    this.aiService.uploadImage(formData)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((result: any) => {
          this.admissionSheetUrl = result.imageUrl;
          return this.aiService.extractPatientDetailsFromImage(this.groupId, this.admissionSheetUrl);
        })
      )
      .subscribe({
        next: (result: any) => {
          // Don't reset admission scanning here - keep the file objects for note creation
          this.admissionSheetUrl = '';
          this.admissionProcessing = false;
          this.patient = result.data;

          if (this.isQuickAdd) {
            this.patient.idType = 'id';
            this.patient.idNumber = '';
            this.patient.passportNumber = '';
            this.patient.liableIdType = 'id';
            this.patient.liableIdNumber = '';
            this.patient.liablePassportNumber = '';
          }

          if (this.patient.authNumber) {
            this.patient.authorizationNumbers = [{
              date: this.patient.authNumberDate || '', authNumber: this.patient.authNumber, comments: ''
            }];
            console.log('*** AUTH NUMBERS: *** ', this.patient.authorizationNumbers);
          }
          this.patchForm();
          void this.linkScannedMedicalAid();
          this.dialog.closeAll();
        },
        error: (error) => {
          this.resetAdmissionScanning();
          this.admissionProcessing = false;
          console.error(error);
          this.feedbackService.showToast('There was an error extracting information from the provided image. Please try again.');
        }
      });
  }

  /**
   * File extension for an image MIME type, or null when the format is not one we accept.
   * The extension matters beyond cosmetics: the server derives the blob's stored
   * Content-Type from the uploaded file name, so a missing or wrong one leaves the
   * image unusable when it is downloaded again.
   */
  private imageExtension(contentType: string): string | null {
    if (contentType === 'image/png') {
      return '.png';
    }
    if (contentType === 'image/jpeg' || contentType === 'image/jpg') {
      return '.jpg';
    }
    return null;
  }

  private async blobToFile(url: string, baseFileName: string) {
    const response = await fetch(url);
    const blob = await response.blob();
    const contentType = blob.type;

    const extension = this.imageExtension(contentType);
    if (!extension) {
      throw new Error('Unsupported image format: ' + contentType);
    }

    return new File([blob], `${baseFileName}${extension}`, { type: contentType });
  }

  private async createAdmissionSheetNote(patient: any): Promise<void> {
    try {
      // Check if we have admission sheet files to process
      if (this.admissionSheetFileObjects.length === 0) {
        return;
      }

      const noteData = {
        date: moment().utc().startOf('day').toISOString(),
        groupId: patientGroupId(patient, this.groupId),
        patientId: patient.id,
        noteType: '',
        noteTypeHeadings: '',
        isProcedure: false,
        procedureCodes: [],
        title: 'Admission Sheet',
        notes: 'Admission sheet used to extract details when adding this patient',
        existingMedia: []
      };

      const formData = new FormData();
      formData.append('data', JSON.stringify(noteData));

      // Add the admission sheet image to the form data
      const mediaAppendPromises = this.admissionSheetFileObjects.map((obj, index) => {
        return fetch(obj.url)
          .then(response => response.blob())
          .then(blob => {
            // Take the extension from the blob being uploaded rather than from the
            // file object, so the saved copy downloads as a .jpg/.png and not as an
            // extensionless file the browser cannot identify.
            const extension = this.imageExtension(blob.type) ?? '.jpg';
            formData.append(`newMedia${index}`, blob, `admission_sheet_${moment().unix()}${extension}`);
          });
      });

      await Promise.all(mediaAppendPromises);

      this.patientNoteService.addNote(formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            console.log('Admission sheet note created successfully');
            // Clear the admission sheet file objects after successful note creation
            this.admissionSheetFileObjects = [];
          },
          error: (error) => {
            console.error('Error creating admission sheet note: ', error);
          }
        });

    } catch (error) {
      console.error('Error creating admission sheet note: ', error);
    }
  }

  private clearZ00CodesAndUnspecifiedDiagnosis() {
    // Filter out Z00 codes from selected ICD codes
    this.selectedIcdCodes = this.selectedIcdCodes.filter(code => !code.code.startsWith('Z00'));
    this.form.patchValue({ icdCodes: this.selectedIcdCodes });

    // Clear diagnosis if it's UNSPECIFIED
    if (this.patient.diagnosis?.toLowerCase() === 'unspecified') {
      this.form.patchValue({ diagnosis: '' });
    }

    // Clear additional diagnosis fields if they contain UNSPECIFIED
    for (let i = 2; i <= 24; i++) {
      const fieldName = `diagnosis${i}`;
      if (this.patient[fieldName]?.toLowerCase() === 'unspecified') {
        this.form.patchValue({ [fieldName]: '' });
      }
    }

    // Mark as cleared to prevent doing it again
    this.hasClearedZ00Codes = true;

    // Show a message to the user
    //this.showToastMessage('Z00 codes and unspecified diagnosis cleared automatically');
  }


}